import { AppHeader as BaseAppHeader, AppHeaderSkeleton, useHeaderStyles } from '@awell/ui-kit/components/AppHeader'
import { LinkTab } from '@awell/ui-kit/components/Tabs'
import { type RouteComponentProps, useLocation, useNavigate } from '@reach/router'
import React, { type FC, useEffect, useState } from 'react'
import { t } from 'i18next'
import classnames from 'classnames'
import { AppSwitcher } from '@awell/ui-kit/components/AppSwitcher'
import type { SwitchableApp } from '@awell/ui-kit/components/AppSwitcher/types'
import { environment } from '@awell/libs-web/environment'
import { useStyles } from './useStyles'
import { PathwayNavigationBar } from '../../pages/Pathway/PathwayNavigationBar'
import { isNil } from 'lodash'
import { useStytchMember, useStytchIsAuthorized, useStytchOrganization } from '@stytch/react/b2b'
import { useAnalytics } from 'use-analytics'
import { useTrackCustify } from '@awell/libs-web/analytics'
import { CustifyEvents } from '@awell/libs-web/experiments'
import { useFeatureGate } from '@statsig/react-bindings'
import { Button, Dropdown, Icon, Tooltip } from '@awell-health/design-system'
import { useCaptureDev } from '@awell/libs-web/authentication/useCaptureDev'

export const AppHeader: FC<RouteComponentProps> = () => {
  const classes = useStyles()
  const { appHeader, appHeaderBoxShadow } = useHeaderStyles()
  const { member } = useStytchMember()
  const { organization } = useStytchOrganization()
  const [isCopied, setIsCopied] = useState(false)

  const { isAuthorized: canAccessPatients } = useStytchIsAuthorized('patients', 'read')
  const { isAuthorized: canAccessWorklist } = useStytchIsAuthorized('worklist', 'worklist_read')
  const { value: showCaptureDevPlugin } = useFeatureGate('capture_dev_plugin')
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { identify } = useAnalytics()
  const { trackCustify } = useTrackCustify()
  const { openCaptureDialog } = useCaptureDev()

  const user_id = member?.member_id

  useEffect(() => {
    identify(user_id ?? '', {
      email: member?.email_address,
      company: {
        id: member?.organization_id
      }
    }).catch(e => {
      console.error('Error identifying user', e)
      /* Nothing to do here, if this calls fails there is no need to panic or block the app usage */
    })
  }, [identify, member])

  const isCurrentTabPath = (tabName: string): boolean => pathname.indexOf(tabName) === 0

  const handleAppSwitch = (app: SwitchableApp): void => {
    switch (app) {
      case 'home':
        void trackCustify(CustifyEvents.GO_CARE_TO_CAREOPS)
        void navigate(environment.urls.careops)
        break
      case 'design':
        void trackCustify(CustifyEvents.GO_CARE_TO_STUDIO)
        void navigate(`${environment.urls.pathway_studio}`)
        break
      default:
    }
  }

  if (!member) {
    return <AppHeaderSkeleton />
  }

  const handleLogout = (): void => {
    void navigate('/logout')
  }
  const isPreview = pathname.match(/(\/pathway\/([^/]*))(\/.*)?/)
  const organizationName = organization?.organization_name ?? ''

  const outGoingWebhooksUrl = `${environment.urls.careops}/monitor/webhooks/outgoing`

  return (
    <BaseAppHeader
      className={classnames(appHeader, {
        [appHeaderBoxShadow]: !isPreview
      })}
    >
      {isNil(isPreview) ? (
        <div className={classes.logo}>
          <AppSwitcher
            currentApp="operate"
            onSwitch={handleAppSwitch}
            urls={{
              home: environment.urls.careops,
              design: environment.urls.pathway_studio,
              operate: environment.urls.care
            }}
          />
        </div>
      ) : (
        <></>
      )}

      {!isNil(isPreview) ? <PathwayNavigationBar id={pathname.split('/')[2]} /> : <></>}

      {isNil(isPreview) ? (
        <div className={classes.tabButtons}>
          {canAccessPatients && (
            <>
              <LinkTab
                tabLabel={t('patient_other')}
                selected={isCurrentTabPath('/patients')}
                linkToLocation="patients"
              />
              <LinkTab
                tabLabel={t('activities')}
                selected={isCurrentTabPath('/activities')}
                linkToLocation="activities"
              />
            </>
          )}
          {canAccessWorklist && (
            <LinkTab tabLabel={t('worklists')} selected={isCurrentTabPath('/worklists')} linkToLocation="worklists" />
          )}
        </div>
      ) : (
        <></>
      )}
      <div className={classes.grow} />
      <div className="flex gap-2 items-center">
        <Tooltip datatip={`${t('help')}`} placement="left">
          <Button
            shape="squareLg"
            variant="ghost"
            size={null}
            data-product-tour-target="awell-help-btn"
            rel="noopener"
            onClick={() => window.open(t('link_awell_help'), '_blank')}
          >
            <Icon icon="RiQuestionLine" size={24} />
          </Button>
        </Tooltip>
        <Dropdown
          dataCy="pathway-header-user-menu"
          buttonClassNames="border-none bg-white shadow-none hover:bg-slate-100 "
          buttonLabel={<Icon icon="RiUserFill" size={20} />}
          items={[
            {
              label: t('logged_in_user_email', {
                email: member?.email_address
              }),
              disabled: true,
              onClick: () => {}
            },
            {
              label: (
                <div className="flex items-center gap-1">
                  {organizationName}
                  <Icon icon={isCopied ? 'RiCheckFill' : 'RiFileCopyFill'} size={12} />
                </div>
              ),
              onClick: () => {
                navigator.clipboard.writeText(organizationName)
                setIsCopied(true)
                setTimeout(() => {
                  setIsCopied(false)
                }, 1000)
              }
            },
            {
              label: t('webhook_logs'),
              onClick: () => {
                window.open(outGoingWebhooksUrl, '_blank')
              }
            },
            {
              label: t('logout'),
              onClick: () => {
                handleLogout()
              }
            }
          ]}
          placement="left"
        />
        {showCaptureDevPlugin ? (
          <Button
            variant="secondary"
            onClick={() => {
              openCaptureDialog()
            }}
          >
            {t('report_issue')}
          </Button>
        ) : (
          <></>
        )}
        {canAccessPatients ? (
          <Button
            onClick={() => {
              void navigate('/patients/create')
            }}
            variant="secondaryBlue"
            data-product-tour-target="create-patient-btn"
          >
            {t('create_patient')}
          </Button>
        ) : (
          <></>
        )}
      </div>
    </BaseAppHeader>
  )
}

AppHeader.displayName = 'CareAppHeader'
